import React, { useState, useEffect } from "react";
import axios from "axios";
import { Plus, Loader2, Edit, Trash2 } from "lucide-react";
import Swal from "sweetalert2";
import EditCategoryModal from "../../components/AdminComponents/category/EditCategoryModal";
import CategorySkeleton from "../../components/AdminComponents/category/CategorySkeleton";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import CreateCategoryModal from "../../components/AdminComponents/category/CreateCategoryModal";

const Category = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateModal, setIsCreateModal] = useState(false);
  const [title, setTitle] = useState("");
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryId, setCategoryId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/category/fetch-all`,
        // { withCredentials: true }
      );
      setCategories(response.data);
      setFilteredCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch categories",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleModalClose = () => {
    setIsModalOpen(false);
    setTitle("");
    setCategoryId(null);
    fetchCategories();
  };

  const handleEditCategory = (category) => {
    setTitle(category.title);
    setCategoryId(category._id);
    setIsModalOpen(true);
  };

  const handleDeleteCategory = async (id) => {
    console.log("id", id);
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "This category will be permanently deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#0bb6d9",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (confirm.isConfirmed) {
      console.log("enter");
      try {
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/category/delete/${id}`,
          {
            // withCredentials: true,
          }
        );
        Swal.fire("Deleted!", "Category has been deleted.", "success");

        // Update both categories arrays
        const updatedCategories = categories.filter((cat) => cat._id !== id);
        setCategories(updatedCategories);
        setFilteredCategories(updatedCategories);

        // Adjust current page if needed
        if (currentCategories.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        }
      } catch (error) {
        Swal.fire("Error", "Failed to delete category", "error");
      }
    }
  };

  // Pagination calculations
  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentCategories = filteredCategories.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredCategories.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const getPageNumbers = () => {
    const pages = [];

    if (totalPages <= 5) {
      // Show all pages if 5 or fewer
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage > 3) {
        pages.push("...");
      }

      // Show pages around current page
      const startPage = Math.max(2, currentPage - 1);
      const endPage = Math.min(totalPages - 1, currentPage + 1);

      for (let i = startPage; i <= endPage; i++) {
        if (!pages.includes(i)) {
          pages.push(i);
        }
      }

      if (currentPage < totalPages - 2) {
        pages.push("...");
      }

      // Always show last page
      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <div className="min-h-screen bg-[#fafafa] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="category-container flex md:flex-row flex-col md:items-center md:justify-between gap-4 mb-8">
          {/* Left side: Heading */}
          <div className="text-center md:text-left">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
              <span className="text-[#0bb6d9]">Category</span>
            </h1>
            <p className="text-gray-600">
              Showing {currentCategories.length} of {filteredCategories.length}{" "}
              categories
            </p>
          </div>

          {/* Right side: Create Category Button */}
          <button
            onClick={() => setIsCreateModal(true)}
            className="px-6 py-3 bg-[#0bb6d9] text-white rounded-lg font-semibold hover:bg-[#069ab7] transition flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Create Category
          </button>
        </div>

        {/* Categories List */}
        <div className="overflow-hidden">
          {loading ? (
            <CategorySkeleton />
          ) : (
            <>
              <div className="p-6">
                {currentCategories.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    No categories found. Create your first category!
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {currentCategories.map((category) => (
                      <div
                        key={category._id}
                        className="relative p-6 bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow group duration-500"
                      >
                        <h3 className="text-lg mt-5 group-hover:text-[#0bb6d9] transition-colors duration-500 font-semibold text-gray-800 mb-2">
                          {category.title}
                        </h3>
                        <p className="text-sm text-gray-500 mb-2">
                          Created:{" "}
                          {new Date(category.createdAt).toLocaleDateString()}
                        </p>
                        <div className="absolute top-3 right-3 flex gap-2">
                          {/* Edit Button */}
                          <button
                            onClick={() => handleEditCategory(category)}
                            className="px-3 py-1 bg-[#0bb6d9]  hover:bg-[#069ab7] text-white text-sm rounded"
                          >
                            <Edit className="w-4 h-4" />
                          </button>

                          {/* Delete Button */}
                          <button
                            onClick={() => handleDeleteCategory(category._id)}
                            className="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-end items-center mt-8 space-x-2">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              <FaChevronLeft size={14} /> Prev
            </button>

            {getPageNumbers().map((page, index) => (
              <div key={index}>
                {page === "..." ? (
                  <span className="px-3 py-2 text-gray-500">...</span>
                ) : (
                  <button
                    onClick={() => handlePageChange(page)}
                    className={`px-3 py-2 rounded-md transition-colors ${currentPage === page
                        ? "bg-blue-500 text-white"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      }`}
                  >
                    {page}
                  </button>
                )}
              </div>
            ))}

            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              Next <FaChevronRight size={14} />
            </button>
          </div>
        )}

        {/* Modal */}
        {isModalOpen && (
          <EditCategoryModal
            title={title}
            categoryId={categoryId}
            setTitle={setTitle}
            setIsModalOpen={handleModalClose}
          />
        )}

        {isCreateModal && (
          <CreateCategoryModal
            setIsModalOpen={setIsCreateModal}
            setTitle={setTitle}
            title={title}
          />
        )}
      </div>
    </div>
  );
};

export default Category;
